import React, {useState, useEffect, useCallback} from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TimestampConverter.module.css';

const TimestampConverter: React.FC = () => {
  const { t } = useTranslation();
  const [currentTimestampUnit, setCurrentTimestampUnit] = useState<'seconds' | 'milliseconds'>('seconds');
  const [currentTimestamp, setCurrentTimestamp] = useState<string>('');
  const [isRunning, setIsRunning] = useState<boolean>(true);
  const [timestamp, setTimestamp] = useState<string>('');
  const [timestampToDateUnit, setTimestampToDateUnit] = useState<'seconds' | 'milliseconds'>('seconds');
  const [timestampToDateTimezone, setTimestampToDateTimezone] = useState<string>('Asia/Shanghai');
  const [dateToTimestampUnit, setDateToTimestampUnit] = useState<'seconds' | 'milliseconds'>('seconds');
  const [dateToTimestampTimezone, setDateToTimestampTimezone] = useState<string>('Asia/Shanghai');
  const [dateTime, setDateTime] = useState<string>('');
  const [convertedTimestamp, setConvertedTimestamp] = useState<string>('');
  const [convertedDateTime, setConvertedDateTime] = useState<string>('');
  const [copyMessage, setCopyMessage] = useState<string>('');
  const [copyMessageType, setCopyMessageType] = useState<'success' | 'error'>('success');

  const conversionFailedText = t('timestampConverter.conversionFailedText');

  // 常用时区列表
  const timezones = [
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai' },
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'America/New_York' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
    { value: 'Europe/London', label: 'Europe/London' },
    { value: 'Europe/Paris', label: 'Europe/Paris' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
    { value: 'Asia/Seoul', label: 'Asia/Seoul' },
    { value: 'Australia/Sydney', label: 'Australia/Sydney' },
  ];

  const updateCurrentTimestamp = useCallback(() => {
    if (!isRunning) return;
    
    const newCurrentTimestamp = currentTimestampUnit === 'seconds' 
      ? Math.floor(Date.now() / 1000) 
      : Date.now();
    setCurrentTimestamp(newCurrentTimestamp.toString());
  }, [currentTimestampUnit, isRunning]);

  useEffect(() => {
    // 立即更新一次
    updateCurrentTimestamp();
    
    // 设置定时器，每秒更新一次
    const timer = setInterval(updateCurrentTimestamp, 1000);
    
    // 清理函数，组件卸载时清除定时器
    return () => {
      clearInterval(timer);
    };
  }, [updateCurrentTimestamp]);

  const showCopyMessage = (message: string, type: 'success' | 'error' = 'success') => {
    setCopyMessage(message);
    setCopyMessageType(type);
    setTimeout(() => {
      setCopyMessage('');
    }, 3000);
  };

  const handleToggleUnit = () => {
    setCurrentTimestampUnit(currentTimestampUnit === 'seconds' ? 'milliseconds' : 'seconds');
  };

  const handleCopyCurrentTimestamp = () => {
    if (currentTimestamp) {
      navigator.clipboard.writeText(currentTimestamp);
      showCopyMessage(t('timestampConverter.copySuccessCurrentTimestamp'), 'success');
    }
  };

  const handleToggleRunning = () => {
    setIsRunning(!isRunning);
  };

  const handleTimestampToDate = () => {
    if (!timestamp) {
      showCopyMessage(t('timestampConverter.errorTimestampRequired'), 'error');
      return;
    }
    if (!timestamp.trim()) {
      showCopyMessage(t('timestampConverter.errorTimestampInvalid'), 'error');
      return;
    }

    try {
      const timestampNum = parseInt(timestamp);
      if (isNaN(timestampNum)) {
        showCopyMessage(t('timestampConverter.errorNumberInvalid'), 'error');
        setConvertedDateTime(conversionFailedText);
        return;
      }
      
      const date = new Date(timestampToDateUnit === 'seconds' ? timestampNum * 1000 : timestampNum);
      
      // 检查日期是否有效
      if (isNaN(date.getTime())) {
        showCopyMessage(t('timestampConverter.errorTimestampInvalidValue'), 'error');
        setConvertedDateTime(conversionFailedText);
        return;
      }
      
      // 使用指定时区格式化时间
      const formattedDate = new Intl.DateTimeFormat('en-CA', {
        timeZone: timestampToDateTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).format(date).replace(',', '');
      
      setConvertedDateTime(formattedDate);
    } catch (error) {
      showCopyMessage(t('timestampConverter.errorConversionFailed'), 'error');
      setConvertedDateTime(conversionFailedText);
    }
  };

  const handleDateToTimestamp = () => {
    if (!dateTime) {
      showCopyMessage(t('timestampConverter.errorDateTimeRequired'), 'error');
      return;
    }
    if (!dateTime.trim()) {
      showCopyMessage(t('timestampConverter.errorDateTimeInvalid'), 'error');
      return;
    }

    try {
      // 解析输入的日期时间字符串，假设格式为 YYYY-MM-DD HH:mm:ss
      const dateTimeString = dateTime.includes('T') ? dateTime : dateTime.replace(' ', 'T');
      const date = new Date(dateTimeString);
      
      // 检查日期是否有效
      if (isNaN(date.getTime())) {
        showCopyMessage(t('timestampConverter.errorDateFormatInvalid'), 'error');
        setConvertedTimestamp(conversionFailedText);
        return;
      }
      
      // 获取用户时区偏移
      const userTimezoneOffset = date.getTimezoneOffset() * 60000; // 转换为毫秒
      
      // 获取目标时区的偏移
      const targetDate = new Date(date.toLocaleString('en-US', { timeZone: dateToTimestampTimezone }));
      const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
      const targetTimezoneOffset = targetDate.getTime() - utcDate.getTime();
      
      // 计算最终时间戳
      const adjustedTime = date.getTime() - userTimezoneOffset + targetTimezoneOffset;
      const timestampSeconds = Math.floor(adjustedTime / 1000);
      
      setConvertedTimestamp(
        dateToTimestampUnit === 'seconds' 
          ? timestampSeconds.toString() 
          : adjustedTime.toString()
      );
    } catch (error) {
      showCopyMessage(t('timestampConverter.errorDateConversionFailed'), 'error');
      setConvertedTimestamp(conversionFailedText);
    }
  };

  const handleCopyDateTime = () => {
    if (convertedDateTime && convertedDateTime !== conversionFailedText) {
      navigator.clipboard.writeText(convertedDateTime);
      showCopyMessage(t('timestampConverter.copyConvertedSuccess'), 'success');
    } else {
      showCopyMessage(t('timestampConverter.copyNoContentError'), 'error');
    }
  };

  const handleCopyTimestamp = () => {
    if (convertedTimestamp && convertedTimestamp !== conversionFailedText) {
      navigator.clipboard.writeText(convertedTimestamp);
      showCopyMessage(t('timestampConverter.copyConvertedSuccess'), 'success');
    } else {
      showCopyMessage(t('timestampConverter.copyNoContentError'), 'error');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* 复制提示消息 */}
        {copyMessage && (
          <div className={`${styles.copyMessage} ${copyMessageType === 'success' ? styles.success : styles.error}`}>
            {copyMessage}
          </div>
        )}

        {/* 当前时间戳 */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>{t('timestampConverter.currentTimestampTitle')}</div>
          <div className={styles.currentTimestampWrapper}>
            <div className={styles.timestampValue}>
              {currentTimestamp} {currentTimestampUnit === 'seconds' ? t('timestampConverter.timestampUnitSeconds') : t('timestampConverter.timestampUnitMilliseconds')}
            </div>
            <div className={styles.currentTimestampButtons}>
              <button 
                onClick={handleToggleUnit}
                className={styles.toggleButton}
              >
                {t('timestampConverter.toggleUnit')}
              </button>
              <button 
                onClick={handleCopyCurrentTimestamp}
                className={styles.copyButtonSmall}
              >
                {t('timestampConverter.copyButton')}
              </button>
              <button 
                onClick={handleToggleRunning}
                className={`${styles.stopButton} ${!isRunning ? styles.startButton : ''}`}
              >
                {isRunning ? t('timestampConverter.stopButton') : t('timestampConverter.startButton')}
              </button>
            </div>
          </div>
        </div>

        {/* 时间戳转日期 */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>{t('timestampConverter.timestampToDateTitle')}</div>
          <div className={styles.inputGroup}>
            <input 
              type="text" 
              value={timestamp}
              onChange={(e) => setTimestamp(e.target.value)}
              placeholder={t('timestampConverter.timestampInputPlaceholder')}
              className={styles.timestampInput}
            />
            <div className={styles.unitSelectWrapper}>
              <select 
                className={styles.unitSelect}
                value={timestampToDateUnit}
                onChange={(e) => setTimestampToDateUnit(e.target.value as 'seconds' | 'milliseconds')}
                spellCheck={false}
              >
                <option value="seconds">{t('timestampConverter.unitSecondsLabel')}</option>
                <option value="milliseconds">{t('timestampConverter.unitMillisecondsLabel')}</option>
              </select>
            </div>
            <div className={styles.actionButtons}>
              <button 
                onClick={handleTimestampToDate}
                className={styles.convertButton}
              >
                {t('timestampConverter.convertButton')}
              </button>
            </div>
            <div className={styles.resultActions}>
              <input
                type="text"
                value={convertedDateTime}
                readOnly
                placeholder={t('timestampConverter.resultPlaceholder')}
                className={styles.resultDisplay}
              />
              <div className={styles.timezoneSelectWrapper}>
                <select 
                  className={styles.timezoneSelect}
                  value={timestampToDateTimezone}
                  onChange={(e) => setTimestampToDateTimezone(e.target.value)}
                  spellCheck={false}
                >
                  {timezones.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleCopyDateTime}
                className={styles.copyButton}
              >
                {t('timestampConverter.copyButton')}
              </button>
            </div>
          </div>
        </div>

        {/* 日期时间转时间戳 */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>{t('timestampConverter.dateTimeToTimestampTitle')}</div>
          <div className={styles.inputGroup}>
            <input 
              type="text" 
              value={dateTime}
              onChange={(e) => setDateTime(e.target.value)}
              placeholder={t('timestampConverter.dateTimeInputPlaceholder')}
              className={styles.timestampInput}
            />
            <div className={styles.timezoneSelectWrapper}>
              <select 
                className={styles.timezoneSelect}
                value={dateToTimestampTimezone}
                onChange={(e) => setDateToTimestampTimezone(e.target.value)}
                spellCheck={false}
              >
                {timezones.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
            <div className={styles.unitSelectWrapper}>
              <select 
                className={styles.unitSelect}
                value={dateToTimestampUnit}
                onChange={(e) => setDateToTimestampUnit(e.target.value as 'seconds' | 'milliseconds')}
                spellCheck={false}
              >
                <option value="seconds">{t('timestampConverter.unitSecondsLabel')}</option>
                <option value="milliseconds">{t('timestampConverter.unitMillisecondsLabel')}</option>
              </select>
            </div>
            <div className={styles.actionButtons}>
              <button 
                onClick={handleDateToTimestamp}
                className={styles.convertButton}
              >
                {t('timestampConverter.convertButton')}
              </button>
            </div>
            <div className={styles.resultActions}>
              <input
                type="text"
                value={convertedTimestamp}
                readOnly
                placeholder={t('timestampConverter.resultPlaceholder')}
                className={styles.resultDisplay}
              />
              <button
                onClick={handleCopyTimestamp}
                className={styles.copyButton}
              >
                {t('timestampConverter.copyButton')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimestampConverter;
